import React from 'react';
import { Autocomplete, TextField, Box, CircularProgress } from '@mui/material';
import { Store } from '../../types/Store';
import { getStoreDisplayLabel, getStoreSearchLabel, storeMatchesSearch } from '../../utils/storeHelpers';
import StorefrontIcon from '@mui/icons-material/Storefront';

interface StoreAutocompleteProps {
    stores: Store[] | null | undefined;
    loading?: boolean;
    value: string | number;
    onChange: (storeId: string) => void;
    label: string;
    size?: 'small' | 'medium';
    helperText?: string;
    required?: boolean;
    error?: boolean;
    showStoreTypeIcon?: boolean;
    disabled?: boolean;
}

const isUnsubscribed = (date?: string) => !!date && new Date(date) < new Date();
const isMainEntity = (type?: string) => type === 'COMPANY' || type === 'ROOT';

const StoreAutocomplete = ({
    stores,
    loading = false,
    value,
    onChange,
    label,
    size = 'small',
    helperText,
    required = false,
    error = false,
    showStoreTypeIcon = true,
    disabled = false,
}: StoreAutocompleteProps) => {
    const uniqueStores = React.useMemo(() => {
        if (!stores) return [];
        const seen = new Map<string, Store>();
        stores.forEach(store => {
            const id = store.ENTITY_ID.toString();
            if (!seen.has(id)) seen.set(id, store);
        });
        return Array.from(seen.values());
    }, [stores]);

    const selectedStore = React.useMemo(
        () => uniqueStores.find(s => s.ENTITY_ID.toString() === value.toString()) || null,
        [uniqueStores, value]
    );

    return (
        <Autocomplete
            fullWidth
            size={size}
            disabled={disabled}
            options={uniqueStores}
            value={selectedStore}
            loading={loading}
            onChange={(_e, option) => onChange(option ? String(option.ENTITY_ID) : '')}
            getOptionLabel={getStoreSearchLabel}
            isOptionEqualToValue={(option, val) => option.ENTITY_ID.toString() === val.ENTITY_ID.toString()}
            filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                return options.filter((option) => storeMatchesSearch(option, inputValue));
            }}
            renderOption={(props, option) => (
                <Box
                    component="li"
                    {...props}
                    key={option.ENTITY_ID}
                    sx={{
                        display: 'flex',
                        gap: 1,
                        fontSize: '0.875rem',
                        color: isUnsubscribed(option.UNSUBSCRIBE_DATE) ? 'error.main' : 'inherit',
                        fontWeight: isMainEntity(option.ENTITY_TYPE) ? 600 : 'normal',
                    }}
                >
                    {option.IDENTIFIER || ''} – {getStoreDisplayLabel(option)} {showStoreTypeIcon && option.ENTITY_TYPE === 'STORE' && (
                    <StorefrontIcon fontSize="small" sx={{ color: isUnsubscribed(option.UNSUBSCRIBE_DATE) ? 'error.main' : 'primary.main' }} />
                )}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={error}
                    helperText={helperText}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={16} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            ListboxProps={{ style: { maxHeight: 400 } }}
            noOptionsText="Aucun magasin trouvé"
        />
    );
};

export default StoreAutocomplete;
